import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { ArrowDownIcon } from 'lucide-react';
import { PrimaryButton } from './PrimaryButton';
import { formatMicrograms } from '../../utils/rekovelleDose';
type RevealScreenProps = {
  amh: number;
  weight: number;
  dose: number;
  onNext: () => void;
};
const EASE = [0.23, 1, 0.32, 1] as const;
export function RevealScreen({
  amh,
  weight,
  dose,
  onNext
}: RevealScreenProps) {
  const reduced = useReducedMotion() ?? false;
  const steps = [{
    title: 'AMH + body weight',
    caption: `${amh} pmol/L · ${weight} kg`
  }, {
    title: 'Defined individualized first-cycle starting dose',
    caption: 'Set before stimulation begins',
    dose: true
  }, {
    title: 'Designed around an appropriate ovarian response',
    caption: 'Not maximum stimulation'
  }];
  return <div className="flex h-full flex-col" data-id="element-695" data-mp="1hi95ji2y5cn8">
      <h2 className="text-[32px] font-semibold leading-[1.04] tracking-[-0.035em] text-ink" data-id="element-696" data-mp="1hjaffj887t3d">
        <span className="text-rx" data-id="element-697" data-mp="1coed9q99zpcj">REKOVELLE</span> starts with the patient.
      </h2>

      <ol className="my-auto flex flex-col items-stretch py-5" data-id="element-698" data-mp="3dmfzwpco0udp">
        {steps.map((s, i) => <motion.li key={s.title} initial={reduced ? false : {
        opacity: 0,
        y: 8
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.24,
        ease: EASE,
        delay: reduced ? 0 : 0.1 + i * 0.16
      }} className="flex flex-col items-center" data-id="element-699" data-mp="3hmq17opiiwm8" data-mpi={i}>
            {i > 0 && <ArrowDownIcon size={18} strokeWidth={1.75} className="my-2 text-graphite/60" aria-hidden="true" data-id="element-700" data-mp="2tzro910lq6t2" />}
            {s.dose ? <div className="w-full rounded-2xl bg-rx px-5 py-4 text-center text-white" data-id="element-701" data-mp="2kxifogow8nib">
                <p className="text-[15px] font-semibold leading-tight tracking-[-0.01em]" data-id="element-702" data-mp="2pvsh3917okcd">{s.title}</p>
                <p className="mt-2 text-[34px] font-semibold leading-none tabular-nums tracking-[-0.03em]" data-id="element-703" data-mp="j52pq0cab7e9">{formatMicrograms(dose)}</p>
                <p className="mt-1.5 font-mono text-[9.5px] tracking-[0.12em] text-white/75" data-id="element-704" data-mp="o3dvnzzgkxr7">PER DAY · FOR THIS PATIENT</p>
              </div> : <div className="w-full rounded-2xl border border-ink/10 bg-white px-5 py-3.5 text-center text-ink" data-id="element-705" data-mp="hnxb2mgfzcw4">
                <p className="text-[16px] font-semibold leading-tight tracking-[-0.015em]" data-id="element-706" data-mp="cokf9c7levhe">{s.title}</p>
                <p className="mt-1 font-mono text-[10px] tracking-[0.12em] text-graphite" data-id="element-707" data-mp="7rars23rktw7">{s.caption.toUpperCase()}</p>
              </div>}
          </motion.li>)}
      </ol>

      <p className="text-[15px] leading-snug text-graphite" data-id="element-708" data-mp="10eohbf54un6a">
        The starting dose is determined from patient characteristics before stimulation begins.
      </p>
      <p className="mt-1.5 text-[10px] leading-snug text-graphite/70" data-id="element-709" data-mp="qp4xo79lipad">
        Illustrative calculation following the labelled first-cycle algorithm. Verify against the approved SmPC.
      </p>

      <div className="mt-5" data-id="element-710" data-mp="1o6bxp34rqfub">
        <PrimaryButton onClick={onNext} data-id="element-711" data-mp="1jdivon0opllx">CONTINUE</PrimaryButton>
      </div>
    </div>;
}